import {
    CreateWaypointTranslationParams,
    DeleteWaypointLanguageParams,
    DeleteWaypointParams,
    GetWaitingTemporaryAudioWaypointByIdParams,
    GetWaypointByIdParams,
    GetWaypointLanguagesParams,
    SaveTemporaryWaypointParams,
    WaypointAudioQueueAIParams,
    WaypointQueueAIParams,
} from "../types/requestInputs";
import {
    GetStopByIdResponse,
    GetWaitingTemporaryAudioWaypointByIdResponse,
    GetWaypointByIdResponse,
    GetWaypointLanguagesResponse,
    GetWaypointsResponse,
    SaveTemporaryWaypointResponse,
} from "../types/requestsOutputs";
import { MessageResponse, Picture } from "../types/types";
import { fetchAPI } from "./utils";

// Save a temporary waypoint
export const saveTemporaryWaypoint = async ({
    token,
    internalId,
    trackId,
    waypointId,
    latitude,
    longitude,
    address,
    position,
    language,
    title,
    text,
    audioUrl,
    videoUrl,
    pictures,
    type,
    isReferenceStopForReturnLoop,
    inLoop,
    startDistance,
    isStraightLine,
    triggerMode,
    scannableObjectId,
    localizedContent,
    mediaType,
}: SaveTemporaryWaypointParams): Promise<SaveTemporaryWaypointResponse> => {
    return fetchAPI<SaveTemporaryWaypointResponse>(
        "/temp-waypoint-creation",
        "POST",
        {
            internalId,
            trackId,
            waypointId,
            latitude,
            longitude,
            address,
            position,
            language,
            title,
            text,
            audioUrl,
            videoUrl,
            pictures,
            type,
            isReferenceStopForReturnLoop,
            inLoop,
            startDistance,
            isStraightLine,
            triggerMode,
            scannableObjectId,
            localizedContent,
            mediaType,
        },
        token,
    );
};

export const getWaypointsForTrack = async ({
    token,
    trackId,
}: {
    token: string;
    trackId: string;
}): Promise<GetWaypointsResponse> => {
    return fetchAPI<GetWaypointsResponse>("/get-track-waypoints", "POST", { trackId }, token);
};

// Delete a waypoint from a track
export const deleteWaypoint = async ({
    token,
    trackId,
    waypointId,
}: DeleteWaypointParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-waypoint", "POST", { trackId, waypointId }, token);
};

// Get Stop by ID
export const getStopById = async ({
    token,
    id,
    language,
}: {
    token: string;
    id: string;
    language: string;
}): Promise<GetStopByIdResponse> => {
    return fetchAPI<GetStopByIdResponse>("/get-stop-by-id", "POST", { id, language }, token);
};

// Get Waypoint by ID
export const getWaypointById = async ({
    token,
    id,
    language,
}: GetWaypointByIdParams): Promise<GetWaypointByIdResponse> => {
    let response = await fetchAPI<GetWaypointByIdResponse>("/get-waypoint-by-id", "POST", { id, language }, token);
    const tempWpt = response.waypointTemporary;
    if (tempWpt) {
        const tempPics: any = tempWpt.pictures;
        if (tempPics && tempPics.length > 0) {
            const sortedPics: Picture[] = tempPics.map((url: string, index: number) => ({
                id: "", // No id provided, so leave as empty or generate one if needed.
                url,
                position: index, // You might use index or another value.
                isLSF: false, // Default value, adjust as needed.
                activityId: null,
                trackId: tempWpt.trackId,
                waypointsId: "tempIdbidon",
            }));
            response.waypointTemporary.pictures = sortedPics;
            return response;
        } else {
            return response;
        }
    } else {
        return response;
    }
};

// Create AI Text Queue for a Waypoint
export const waypointQueueAI = async ({
    token,
    waypointId,
    language,
    textObject,
    numberOfWords,
    theme,
    audience,
    textType,
    speakerOne,
    speakerTwo,
}: WaypointQueueAIParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/create-waypoint-queue-item-text",
        "POST",
        { waypointId, language, textObject, numberOfWords, theme, audience, textType, speakerOne, speakerTwo },
        token,
    );
};

// Create AI Audio Queue for a Waypoint
export const waypointAudioQueueAI = async ({
    token,
    waypointId,
    language,
    textType,
    speakerOne,
    speakerTwo,
}: WaypointAudioQueueAIParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/create-waypoint-queue-item-audio",
        "POST",
        { waypointId, language, textType, speakerOne, speakerTwo },
        token,
    );
};

// Get waiting temporary AI-generated waypoint audio
export const getWaitingTemporaryAudioWaypointById = async ({
    token,
    id,
}: GetWaitingTemporaryAudioWaypointByIdParams): Promise<GetWaitingTemporaryAudioWaypointByIdResponse> => {
    return fetchAPI<GetWaitingTemporaryAudioWaypointByIdResponse>(
        "/get-waypoint-queue-item-audio",
        "POST",
        { waypointId: id },
        token,
    );
};

// Create AI Translation for a Waypoint
export const createWaypointTranslation = async ({
    token,
    id,
    language,
    translations,
    isText,
    isAudio,
}: CreateWaypointTranslationParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/create-waypoint-temp-queue-translate",
        "POST",
        { internalId: id, baseLanguage: language, translationParams: translations, isText, isAudio },
        token,
    );
};

// Delete a waypoint from a track
export const deleteWaypointAPI = async ({
    token,
    trackId,
    waypointId,
}: DeleteWaypointParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-waypoint", "POST", { trackId, waypointId }, token);
};

// Get available languages for a waypoint
export const getWaypointLanguages = async ({
    token,
    waypointId,
}: GetWaypointLanguagesParams): Promise<GetWaypointLanguagesResponse> => {
    return fetchAPI<GetWaypointLanguagesResponse>("/get-waypoint-languages", "POST", { waypointId }, token);
};

// Delete a specific language from a waypoint
export const deleteWaypointLanguage = async ({
    token,
    waypointId,
    language,
}: DeleteWaypointLanguageParams): Promise<String> => {
    return fetchAPI<String>("/delete-waypoint-language", "POST", { waypointId, language }, token);
};
